import type { IconProps } from 'react-toastify/dist/components/Icons'

/**
 * Ícones custom para os toasts (variação "Anel + barra no topo").
 *
 * Cada tipo de toast recebe um ícone dentro de um anel circular tingido com o
 * wash da cor de status correspondente. As cores seguem os tokens semânticos do
 * DESIGN.md:
 *   success #1A803C · error #CC2942 · alert #B27C30 · info #366AB2
 * O mint (#01F7C2) é reservado para ação/marca e nunca aparece como status.
 *
 * Usado via a prop `icon` do <ToastContainer/>, o que permite estilizar todos os
 * 211 call sites existentes (que passam apenas strings) sem alterá-los.
 */

type Tone = {
  color: string
  wash: string
}

const TONES: Record<string, Tone> = {
  success: { color: '#1A803C', wash: '#E9F7EE' },
  error: { color: '#CC2942', wash: '#FBECEE' },
  warning: { color: '#B27C30', wash: '#F8F0E2' },
  info: { color: '#366AB2', wash: '#E9F1FB' },
  default: { color: '#646664', wash: '#EEF1EE' }
}

const PATHS: Record<string, JSX.Element> = {
  success: <path d="M20 6 9 17l-5-5" />,
  error: <path d="M18 6 6 18M6 6l12 12" />,
  warning: (
    <>
      <path d="M12 9v4M12 17h.01" />
      <path d="M10.3 3.3 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.3a2 2 0 0 0-3.4 0Z" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4M12 8h.01" />
    </>
  ),
  default: (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4M12 8h.01" />
    </>
  )
}

const RING = 34
const ICON = 18

const Ring = ({ type }: { type: string }) => {
  const tone = TONES[type] ?? TONES.default
  const path = PATHS[type] ?? PATHS.default

  return (
    <span
      style={{
        display: 'grid',
        placeItems: 'center',
        width: RING,
        height: RING,
        borderRadius: '50%',
        background: tone.wash,
        flexShrink: 0
      }}
    >
      <svg
        viewBox="0 0 24 24"
        width={ICON}
        height={ICON}
        fill="none"
        stroke={tone.color}
        strokeWidth={2.2}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {path}
      </svg>
    </span>
  )
}

/**
 * Spinner para toast.loading — mesmo anel, com o traço em rotação.
 */
const Spinner = () => (
  <span
    style={{
      display: 'grid',
      placeItems: 'center',
      width: RING,
      height: RING,
      borderRadius: '50%',
      background: TONES.default.wash,
      flexShrink: 0
    }}
  >
    <svg
      viewBox="0 0 24 24"
      width={ICON}
      height={ICON}
      fill="none"
      stroke={TONES.default.color}
      strokeWidth={2.4}
      strokeLinecap="round"
      className="cb-toast-spin"
    >
      <path d="M21 12a9 9 0 1 1-6.2-8.6" />
    </svg>
  </span>
)

export const toastIcon = ({ type, isLoading }: IconProps) => {
  if (isLoading) return <Spinner />
  return <Ring type={type} />
}
